import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useSectionCompletionStore,
  sectionKey,
} from "@/stores/sectionCompletionStore";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseGlossaryStore } from "@/stores/courseGlossaryStore";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { stripThirdLevelNumbering } from "@/lib/courseUtils";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import type { Storyboard } from "@/types/storyboard";
import type { GlossaryTerm } from "@/types/glossary";

interface ChapterOutroContent {
  title: string;
  body: string;
  ctaLabel: string;
  onNext: () => void;
  onOutroSeen: () => void;
  lastSubchapter: CourseTreeNode | null;
  contentTitle?: string;
  contentElements: CourseTreeNode[];
  storyboards: Storyboard[];
  storyboardsLoading: boolean;
  glossaryTerms: GlossaryTerm[];
}

/**
 * Prefetches last-subchapter storyboards and builds props for the chapter outro stack.
 */
const useChapterOutroContent = (
  chapter: CourseTreeNode,
  outroFallback: string
): ChapterOutroContent => {
  const navigate = useNavigate();
  const basePath = useCourseBasePath();
  const { enrollmentId } = useParams<{ enrollmentId?: string }>();
  const markCompleted = useSectionCompletionStore(
    (state) => state.markCompleted
  );
  const courseData = useCourseDetailStore((state) => state.courseData);
  const nodeIndex = useCourseDetailStore((state) => state.nodeIndex);
  const fetchStoryboardsBySubChapter = useCourseDetailStore(
    (state) => state.fetchStoryboardsBySubChapter
  );
  const storyboards = useCourseDetailStore((state) => state.storyboards);
  const storyboardsLoading = useCourseDetailStore(
    (state) => state.storyboardsLoading
  );
  const storyboardSubChapterId = useCourseDetailStore(
    (state) => state.storyboardSubChapterId
  );
  const glossaryTerms = useCourseGlossaryStore((state) => state.terms);

  const subchapters =
    chapter.children?.filter(
      (child) => child.type === CourseTreeNodeTypeEnum.Subchapter
    ) ?? [];
  const lastSubchapter =
    subchapters.length > 0 ? subchapters[subchapters.length - 1] : null;
  const lastSubchapterId = lastSubchapter?.id;

  const contentElements = lastSubchapterId
    ? (nodeIndex.get(lastSubchapterId)?.contentElements ?? [])
    : [];

  const contentReady =
    lastSubchapterId != null && storyboardSubChapterId === lastSubchapterId;

  const { nextTarget, ctaLabel, nextQuery } = useMemo(() => {
    const entry = nodeIndex.get(chapter.id);
    const module = entry?.module;
    if (!module || !courseData?.structure) {
      return { nextTarget: null, ctaLabel: "Kurs abschließen", nextQuery: "" };
    }

    const chapters = module.children?.filter((c) => c.type === "chapter") ?? [];
    const chapterIdx = chapters.findIndex((c) => c.id === chapter.id);
    const isLastChapter = chapterIdx === chapters.length - 1;

    if (!isLastChapter) {
      return {
        nextTarget: chapters[chapterIdx + 1],
        ctaLabel: "Nächstes Kapitel",
        nextQuery: "",
      };
    }

    // Last chapter → current module Abschluss (not the next module).
    return {
      nextTarget: module,
      ctaLabel: "Modul abschließen",
      nextQuery: "?view=outro",
    };
  }, [chapter.id, courseData, nodeIndex]);

  useEffect(() => {
    if (!lastSubchapterId) return;
    void fetchStoryboardsBySubChapter(lastSubchapterId);
  }, [lastSubchapterId, fetchStoryboardsBySubChapter]);

  const onOutroSeen = () => markCompleted(sectionKey("outro", chapter.id));

  const onNext = () => {
    onOutroSeen();
    if (nextTarget) {
      navigate(`${basePath}/${nextTarget.id}${nextQuery}`);
    } else if (enrollmentId) {
      navigate(`/course/${enrollmentId}/dashboard`);
    } else {
      navigate("/");
    }
  };

  return {
    title: chapter.title,
    body: chapter.outro || outroFallback,
    ctaLabel,
    onNext,
    onOutroSeen,
    lastSubchapter,
    contentTitle: lastSubchapter
      ? stripThirdLevelNumbering(lastSubchapter.title)
      : undefined,
    contentElements,
    storyboards: contentReady ? storyboards : [],
    storyboardsLoading:
      Boolean(lastSubchapterId) && (!contentReady || storyboardsLoading),
    glossaryTerms,
  };
};

export { useChapterOutroContent };
